import React, { useState, useEffect } from "react";
import { Question, QuestionBank, Section, Trait, Scale, QuestionTypeMaster } from "../../types";
import { useApp } from "../../context/AppContext";
import { getQuestionBanks, createQuestionBank, updateQuestionBank, deleteQuestionBank, getBanksBySection } from "../../services/questionBankService";
import { getQuestions, createQuestion, updateQuestion, deleteQuestion } from "../../services/questionService";
import { getSections, getSectionById } from "../../services/sectionService";
import { getTraits } from "../../services/traitService";
import { getScales } from "../../services/scaleService";
import { getQuestionTypes } from "../../services/questionTypeService";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import { Input, Select } from "../../components/ui/FormFields";
import QuestionBankModal from "../../components/questions/QuestionBankModal";
import QuestionModal from "../../components/questions/QuestionModal";
import BankCard from "../../components/questions/BankCard";
import BulkUploadModal from "../../components/questions/BulkUploadModal";
import { toast } from "react-hot-toast";

const QuestionsPage: React.FC = () => {
  const { currentUser: contextUser } = useApp();

  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [traits, setTraits] = useState<Trait[]>([]);
  const [scales, setScales] = useState<Scale[]>([]);
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedBanks, setExpandedBanks] = useState<Set<string>>(new Set());
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [selectedBankForQuestion, setSelectedBankForQuestion] = useState<string>("");

  const [showBankModal, setShowBankModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedBankForBulk, setSelectedBankForBulk] = useState<QuestionBank | null>(null);
  const [editBank, setEditBank] = useState<QuestionBank | null>(null);
  const [editQuestion, setEditQuestion] = useState<Question | null>(null);
  const [deleteBank, setDeleteBank] = useState<QuestionBank | null>(null);
  const [deleteQuestionItem, setDeleteQuestionItem] = useState<Question | null>(null);

  const [sectionFilter, setSectionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  const [questionTypeFilter, setQuestionTypeFilter] = useState("all");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (sectionFilter !== "all") {
      loadBanksForSection(sectionFilter);
    } else {
      loadAllBanks();
    }
  }, [sectionFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedSections, fetchedTraits, fetchedScalesResponse, fetchedQuestionTypes] = await Promise.all([
        getSections(),
        getTraits(),
        getScales(),
        getQuestionTypes(false)
      ]);
      setSections(fetchedSections);
      setTraits(fetchedTraits);

      const scalesArray = fetchedScalesResponse.scales || fetchedScalesResponse;
      setScales(scalesArray as any);

      setQuestionTypes(fetchedQuestionTypes);

      const fetchedBanks = await getQuestionBanks();
      setBanks(fetchedBanks);

      const fetchedQuestions = await getQuestions();
      setQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadAllBanks = async () => {
    try {
      const fetchedBanks = await getQuestionBanks();
      setBanks(fetchedBanks);
      const fetchedQuestions = await getQuestions();
      setQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error loading banks:", error);
    }
  };

  const loadBanksForSection = async (sectionId: string) => {
    try {
      const section = await getSectionById(sectionId);
      setSelectedSection(section);
      const fetchedBanks = await getBanksBySection(sectionId);
      setBanks(fetchedBanks);
      const fetchedQuestions = await getQuestions();
      setQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error loading banks for section:", error);
    }
  };

  const handleSaveBank = async (bankData: any) => {
    try {
      const bankPayload = {
        name: bankData.name,
        description: bankData.description || "",
        sectionId: bankData.sectionId,
        difficultyLevel: "medium" as const,
        language: "english",
        totalQuestions: 0,
        status: "active" as const,
        createdBy: contextUser?.id || "system"
      };

      if (editBank) {
        await updateQuestionBank(editBank.id, bankPayload);
        toast.success("Bank updated successfully");
      } else {
        await createQuestionBank(bankPayload, contextUser?.id || "system");
        toast.success("Bank created successfully");
      }

      if (sectionFilter !== "all") {
        await loadBanksForSection(sectionFilter);
      } else {
        await loadAllBanks();
      }

      setShowBankModal(false);
      setEditBank(null);
    } catch (error) {
      console.error("Error saving bank:", error);
      toast.error("Failed to save bank");
      throw error;
    }
  };

  const handleSaveQuestion = async (questionData: any) => {
    try {
      if (editQuestion) {
        await updateQuestion(editQuestion.id, questionData);
        toast.success("Question updated successfully");
      } else {
        await createQuestion(questionData, contextUser?.id || "system");
        toast.success("Question created successfully");
      }

      if (sectionFilter !== "all") {
        await loadBanksForSection(sectionFilter);
      } else if (questionData.bankId) {
        const bank = banks.find(b => b.id === questionData.bankId);
        if (bank && bank.sectionId) {
          await loadBanksForSection(bank.sectionId);
        } else {
          await loadAllBanks();
        }
      } else {
        await loadAllBanks();
      }

      setShowQuestionModal(false);
      setEditQuestion(null);
      setSelectedBankForQuestion("");
      if (questionData.bankId) {
        setExpandedBanks(prev => new Set(prev).add(questionData.bankId));
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Failed to save question");
      throw error;
    }
  };

  const handleBulkUpload = async (questionsData: any[]) => {
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const questionData of questionsData) {
        try {
          await createQuestion(questionData, contextUser?.id || "system");
          successCount++;
        } catch (error) {
          errorCount++;
          console.error("Error creating question:", questionData.text, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} questions${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
      } else {
        toast.error("Failed to upload questions");
      }

      if (sectionFilter !== "all") {
        await loadBanksForSection(sectionFilter);
      } else if (selectedBankForBulk) {
        const bank = banks.find(b => b.id === selectedBankForBulk.id);
        if (bank && bank.sectionId) {
          await loadBanksForSection(bank.sectionId);
        } else {
          await loadAllBanks();
        }
      } else {
        await loadAllBanks();
      }

      if (selectedBankForBulk) {
        setExpandedBanks(prev => new Set(prev).add(selectedBankForBulk.id));
      }

      setShowBulkUploadModal(false);
      setSelectedBankForBulk(null);
    } catch (error) {
      console.error("Error in bulk upload:", error);
      toast.error("Failed to process bulk upload");
      throw error;
    }
  };

  const openNewBank = () => {
    setEditBank(null);
    setShowBankModal(true);
  };

  const openEditBank = (bank: QuestionBank) => {
    setEditBank(bank);
    setShowBankModal(true);
  };

  const openNewQuestion = (bankId?: string) => {
    if (bankId) {
      setSelectedBankForQuestion(bankId);
    } else if (banks.length > 0) {
      setSelectedBankForQuestion(banks[0].id);
    } else {
      toast.error("Please create a question bank first");
      return;
    }
    setEditQuestion(null);
    setShowQuestionModal(true);
  };

  const openBulkUpload = (bank: QuestionBank) => {
    setSelectedBankForBulk(bank);
    setShowBulkUploadModal(true);
  };

  const openEditQuestion = (question: Question) => {
    setEditQuestion(question);
    setSelectedBankForQuestion(question.bankId);
    setShowQuestionModal(true);
  };

  const toggleBankExpansion = (bankId: string) => {
    setExpandedBanks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bankId)) {
        newSet.delete(bankId);
      } else {
        newSet.add(bankId);
      }
      return newSet;
    });
  };

  const getQuestionsByBank = (bankId: string) => {
    return questions.filter(q => q.bankId === bankId);
  };

  const getBankSection = (bank: QuestionBank) => {
    return sections.find(s => s.id === bank.sectionId);
  };

  const filteredBanks = banks.filter(bank => {
    const bankSection = getBankSection(bank);
    if (typeFilter !== "all" && bankSection?.sectionType !== typeFilter) return false;
    if (searchTerm && !bank.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (difficultyFilter !== "all" || questionTypeFilter !== "all") {
      const bankQuestions = getQuestionsByBank(bank.id);
      if (bankQuestions.length === 0) return false;

      let hasMatchingQuestions = false;
      for (const question of bankQuestions) {
        if (difficultyFilter !== "all" && question.difficulty !== difficultyFilter) continue;
        if (questionTypeFilter !== "all" && question.type !== questionTypeFilter) continue;
        hasMatchingQuestions = true;
        break;
      }
      return hasMatchingQuestions;
    }

    return true;
  });

  const totalQuestions = questions.length;
  const activeBanks = banks.filter(b => b.status === "active").length;

  const typeOptions = [
    { value: "all", label: "All Types" },
    { value: "psychometric", label: "Psychometric" },
    { value: "aptitude", label: "Aptitude" },
    { value: "hybrid", label: "Hybrid" }
  ];

  const sectionOptions = [
    { value: "all", label: "All Sections" },
    ...sections.map(s => ({ value: s.id, label: s.name }))
  ];

  const difficultyOptions = [
    { value: "all", label: "All Difficulties" },
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" }
  ];

  const questionTypeOptions = [
    { value: "all", label: "All Types" },
    ...questionTypes.map(qt => ({ value: qt.id, label: qt.name }))
  ];

  return (
    <PageShell
      title="Question Bank Management"
      subtitle="Create question banks and manage questions"
      action={{ label: "New Question Bank", onClick: openNewBank }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Question Banks" value={banks.length} icon="📚" color="#6366f1" />
        <StatCard label="Active Banks" value={activeBanks} icon="✅" color="#10b981" />
        <StatCard label="Total Questions" value={totalQuestions} icon="❓" color="#f59e0b" />
        <StatCard label="Sections" value={sections.length} icon="📋" color="#8b5cf6" />
      </div>

      <GlassCard className="p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input
              label="Search"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search banks..."
            />
          </div>
          <div className="w-64">
            <Select
              label="Section"
              value={sectionFilter}
              onChange={setSectionFilter}
              options={sectionOptions}
            />
          </div>
          <div className="w-64">
            <Select
              label="Section Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
            />
          </div>
          <div className="w-48">
            <Select
              label="Difficulty"
              value={difficultyFilter}
              onChange={setDifficultyFilter}
              options={difficultyOptions}
            />
          </div>
          <div className="w-48">
            <Select
              label="Question Type"
              value={questionTypeFilter}
              onChange={setQuestionTypeFilter}
              options={questionTypeOptions}
            />
          </div>
          <div>
            <Button
              variant="primary"
              onClick={() => openNewQuestion()}
              className="bg-gradient-to-r from-green-500 to-emerald-600"
            >
              + Add Question
            </Button>
          </div>
        </div>
      </GlassCard>

      {sectionFilter !== "all" && selectedSection && (
        <GlassCard className="p-5 mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                  <span className="text-xl">📋</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{selectedSection.name}</h2>
                  <p className="text-sm text-gray-600 capitalize">{selectedSection.sectionType === 'mixed' ? 'Hybrid' : selectedSection.sectionType} Section</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Total Questions</p>
                  <p className="text-2xl font-bold text-indigo-600">{selectedSection.questionCount}</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-2xl font-bold text-indigo-600">{selectedSection.duration} min</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Weightage</p>
                  <p className="text-2xl font-bold text-indigo-600">{selectedSection.weightage}%</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500">Passing Score</p>
                  <p className="text-2xl font-bold text-indigo-600">{selectedSection.passingScore}%</p>
                </div>
              </div>

              {selectedSection.instructions && (
                <div className="mt-4 p-3 bg-white rounded-lg">
                  <p className="text-xs font-medium text-gray-500 mb-1">Instructions:</p>
                  <p className="text-sm text-gray-700">{selectedSection.instructions}</p>
                </div>
              )}

              {selectedSection.allowedQuestionTypes && selectedSection.allowedQuestionTypes.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs font-medium text-gray-600">Allowed Types:</span>
                  {selectedSection.allowedQuestionTypes.map(typeCode => {
                    const qt = questionTypes.find(t => t.code === typeCode);
                    return qt ? (
                      <span key={typeCode} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                        {qt.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            <div className="flex gap-2 ml-4">
              <Button
                variant="primary"
                size="sm"
                onClick={openNewBank}
              >
                + New Bank
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openNewQuestion()}
              >
                + Add Question
              </Button>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        ) : filteredBanks.length === 0 ? (
          <GlassCard className="p-12 text-center">
            <div className="text-6xl mb-4">📚</div>
            <p className="text-gray-500 text-lg">
              {sectionFilter !== "all"
                ? `No question banks found for "${selectedSection?.name}".`
                : `No question banks found.`}
            </p>
            <div className="flex gap-3 justify-center mt-4">
              <Button variant="primary" onClick={openNewBank}>
                Create Question Bank
              </Button>
              {sections.length > 0 && sectionFilter === "all" && (
                <Button variant="secondary" onClick={() => setSectionFilter(sections[0].id)}>
                  Select a Section
                </Button>
              )}
            </div>
          </GlassCard>
        ) : (
          filteredBanks.map(bank => {
            const bankSection = getBankSection(bank);
            return (
              <BankCard
                key={bank.id}
                bank={bank}
                section={bankSection}
                questions={getQuestionsByBank(bank.id)}
                questionTypes={questionTypes}
                isExpanded={expandedBanks.has(bank.id)}
                onToggleExpand={() => toggleBankExpansion(bank.id)}
                onEdit={() => openEditBank(bank)}
                onDelete={() => setDeleteBank(bank)}
                onAddQuestion={() => openNewQuestion(bank.id)}
                onBulkUpload={() => openBulkUpload(bank)}
                onEditQuestion={openEditQuestion}
                onDeleteQuestion={setDeleteQuestionItem}
              />
            );
          })
        )}
      </div>

      <QuestionBankModal
        isOpen={showBankModal}
        onClose={() => { setShowBankModal(false); setEditBank(null); }}
        onSave={handleSaveBank}
        editingBank={editBank}
        sections={sections}
        traits={traits}
        questionTypes={questionTypes}
        preselectedSectionId={sectionFilter !== "all" ? sectionFilter : undefined}
      />

      <QuestionModal
        isOpen={showQuestionModal}
        onClose={() => { setShowQuestionModal(false); setEditQuestion(null); setSelectedBankForQuestion(""); }}
        onSave={handleSaveQuestion}
        editingQuestion={editQuestion}
        banks={banks}
        selectedBankId={selectedBankForQuestion}
        traits={traits}
        sections={sections}
        questionTypes={questionTypes}
      />

      {selectedBankForBulk && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => { setShowBulkUploadModal(false); setSelectedBankForBulk(null); }}
          onUpload={handleBulkUpload}
          bankId={selectedBankForBulk.id}
          bankName={selectedBankForBulk.name}
          questionTypes={questionTypes}
          traits={traits}
          scales={scales}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteBank}
        onClose={() => setDeleteBank(null)}
        onConfirm={async () => {
          await deleteQuestionBank(deleteBank!.id);
          if (sectionFilter !== "all") {
            await loadBanksForSection(sectionFilter);
          } else {
            await loadAllBanks();
          }
          setDeleteBank(null);
          toast.success("Bank deleted");
        }}
        title="Delete Bank"
        message={`Delete "${deleteBank?.name}"? All questions in this bank will also be deleted.`}
      />

      <ConfirmDialog
        isOpen={!!deleteQuestionItem}
        onClose={() => setDeleteQuestionItem(null)}
        onConfirm={async () => {
          await deleteQuestion(deleteQuestionItem!.id, deleteQuestionItem!.bankId);
          if (sectionFilter !== "all") {
            await loadBanksForSection(sectionFilter);
          } else {
            await loadAllBanks();
          }
          setDeleteQuestionItem(null);
          toast.success("Question deleted");
        }}
        title="Delete Question"
        message="Permanently delete this question?"
      />
    </PageShell>
  );
};

export default QuestionsPage;